import React, { useState, useEffect, useRef } from 'react';
import { Printer, Download, ArrowLeft, RefreshCw } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const PurchaseReturnReportPrint = () => {
  const printRef = useRef();
  const [reportData, setReportData] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportId, setReportId] = useState(null);

  // Company data
  const companyData = {
    name: "Nissi Infotech",
    subtitle: "Nissi",
    tagline: "CIVIL CONSTRUCTION",
    address: "3/42-12, Nagappa colony, kavundampalayam, Salem-636030, Tamil Nadu, India",
    phone: "Phone No. 9003744644",
    email: "infraconsoft-implementation@nissiinfotech.in"
  };
  const location = useLocation();
  // Extract ID from URL and fetch data
  useEffect(() => {
    const extractIdFromUrl = () => {
    
   // ✅ Extract id from query string
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get("id");// Default to 1 if no ID found
      
      if (!isNaN(id) && id !== '') {
        setReportId(id);
        fetchPurchaseReturnData(id);
      } else {
        setError('Invalid report ID');
        setLoading(false);
      }
    };

    extractIdFromUrl();
  }, []);

  // Fetch data from API
  const fetchPurchaseReturnData = async (id) => {
    try {
      setLoading(true);
      setError(null);

      const apiUrl = `https://www.nissi-india.com/api/getPurchaseReturnRpt_Print/${id}`;
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const res = await response.json();
      const data=res.data[0];
      console.log("data", data);
      
      // Transform and set the data
      if (data) {
        // Set main report data
        setReportData({
          purReturnNo: data.fld_purchaseretno || data.pur_return_no || '',
          purReturnDate: data.ReturnDate || data.pur_return_date || '',
          branchName: data.fld_branchname || data.branch_name || '',
          remarks: data.fld_remarks || '',
          grnNo: data.fld_grnno || data.grn_no || '',
          grnDate: data.grnDate || data.grn_date || ''
        });

        // Transform items data
        const itemsArray =  res.data ;
        const transformedItems = Array.isArray(itemsArray) ? itemsArray.map((item, index) => ({
          sno: index + 1,
          itemCode: item.fld_itemcode || item.item_code || '-',
          itemName: item.fld_itemname|| item.item_name || '-',
          uom: item.fld_uomname || item.unit || '-',
          ordQty: item.fld_orderqty || item.order_qty || item.ordered_qty || '0',
          grnQty: item.fld_grnqty|| item.grn_qty || item.received_qty || '0',
          accpQty: item.fld_acceptedqty || item.accepted_qty || item.accept_qty || '0',
          rejQty: item.RejectedQty || item.rejected_qty || item.reject_qty || '0',
          retQty: item.ReturnQty || item.return_qty || item.returned_qty || '0',
          unitPrice: item.UnitPrice || item.unit_price || item.rate || '0.00',
          amount: item.Amount || item.total || '0.00'
        })) : [];

        setItems(transformedItems);
      }
    } catch (err) {
      console.error('Error fetching purchase return data:', err);
      setError(`Failed to fetch data: ${err.message}`);
      
      // Set sample data for demonstration on error
      setReportData({
        purReturnNo: 'PR-2024-001',
        purReturnDate: '15/08/2024',
        branchName: 'Main Branch',
        remarks: 'Quality issues found',
        grnNo: 'GRN-2024-001',
        grnDate: '10/08/2024'
      });
      
      setItems([
        {
          sno: 1,
          itemCode: 'ITM001',
          itemName: 'Steel Rods 12mm',
          uom: 'PCS',
          ordQty: '100',
          grnQty: '100',
          accpQty: '95',
          rejQty: '5',
          retQty: '5',
          unitPrice: '150.00',
          amount: '750.00'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate total amount
  const calculateTotal = () => {
    return items.reduce((total, item) => {
      const amount = parseFloat(item.amount) || 0;
      return total + amount;
    }, 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Handle refresh
  const handleRefresh = () => {
    if (reportId) {
      fetchPurchaseReturnData(reportId);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    console.log("Download functionality would be implemented here");
  };

  const handleBack = () => {
    console.log("Navigate back");
  };

  return (
    <div className="min-h-screen bg-white print:bg-white">
      {/* Print styles */}
      <style jsx>{`
        @media print {
          @page {
            margin: 0.5in;
            size: A4;
          }
          
          .no-print {
            display: none !important;
          }
          
          body {
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
          
          * {
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
        }
      `}</style>

      {/* Header Controls - Hidden in print */}
      <div className="print:hidden bg-gray-50 shadow-sm border-b p-4 no-print">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
              >
                <ArrowLeft size={18} />
                Back
              </button>
              
              {/* API Status Indicator */}
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600">Report ID: {reportId}</span>
                {loading && <span className="text-blue-600">Loading...</span>}
                {error && <span className="text-red-600">API Error</span>}
                {!loading && !error && (
                  <span className="text-green-600">
                    Loaded {items.length} items
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded text-sm transition-colors disabled:opacity-50"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
              
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white hover:bg-green-700 rounded text-sm transition-colors"
              >
                <Download size={16} />
                PDF
              </button>
              
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-3 py-2 bg-gray-800 text-white hover:bg-gray-900 rounded text-sm transition-colors"
              >
                <Printer size={16} />
                Print
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Print Content */}
      <div className="max-w-4xl mx-auto print:max-w-full print:mx-0 p-4 print:p-0">
        <div
          ref={printRef}
          className="bg-white print:bg-white border-2 border-black print:border-2 print:border-black"
        >
          {/* Company Header */}
          <div className="border-b-2 border-black p-4">
            <div className="flex items-start gap-4">
              {/* Company Logo */}
              <div className="w-20 h-16 bg-black flex items-center justify-center flex-shrink-0">
                <div className="text-white text-xs font-bold text-center">
                  <div>NISSI</div>
                  <div>INFOTECH</div>
                  <div>PVT LTD</div>
                </div>
              </div>
              
              {/* Company Details */}
              <div className="flex-1">
                <h1 className="text-xl font-bold text-black mb-1">{companyData.name}</h1>
                <p className="text-sm font-semibold text-black mb-1">{companyData.subtitle}</p>
                <p className="text-sm font-bold text-black mb-2">{companyData.tagline}</p>
                <p className="text-xs text-black mb-1">{companyData.address}</p>
                <div className="text-xs text-black">
                  <span className="mr-6">{companyData.phone}</span>
                  <span>{companyData.email}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Document Title */}
          <div className="text-center py-3 border-b-2 border-black">
            <h2 className="text-lg font-bold underline text-black">Purchase Return Report</h2>
          </div>

          {/* Loading/Error States */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mx-auto mb-2"></div>
                <p className="text-sm">Loading purchase return data...</p>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="flex items-center justify-center py-8 print:hidden">
              <div className="text-center text-red-600">
                <p className="text-sm mb-2">Error loading data</p>
                <p className="text-xs mb-2">{error}</p>
                <button 
                  onClick={handleRefresh}
                  className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* Report Content */}
          {!loading && reportData && (
            <>
              {/* Report Details */}
              <div className="p-4">
                <div className="grid grid-cols-2 gap-8 mb-4">
                  <div className="space-y-2">
                    <div className="flex">
                      <span className="font-semibold text-sm w-32">Pur Return No. :</span>
                      <span className="text-sm">{reportData.purReturnNo}</span>
                    </div>
                    <div className="flex">
                      <span className="font-semibold text-sm w-32">BranchName :</span>
                      <span className="text-sm">{reportData.branchName}</span>
                    </div>
                    <div className="flex">
                      <span className="font-semibold text-sm w-32">Grn No :</span>
                      <span className="text-sm">{reportData.grnNo}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex">
                      <span className="font-semibold text-sm w-32">Pur Return Date :</span>
                      <span className="text-sm">{reportData.purReturnDate}</span>
                    </div>
                    <div className="flex">
                      <span className="font-semibold text-sm w-32">Remarks :</span>
                      <span className="text-sm">{reportData.remarks}</span>
                    </div>
                    <div className="flex">
                      <span className="font-semibold text-sm w-32">Grn Date :</span>
                      <span className="text-sm">{reportData.grnDate}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Items Table */}
              <div className="border-t border-black">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-black">
                      <th className="border-r border-black px-2 py-2 text-xs font-bold text-center">S.No</th>
                      <th className="border-r border-black px-2 py-2 text-xs font-bold text-center">Item Code</th>
                      <th className="border-r border-black px-2 py-2 text-xs font-bold text-center">Item Name</th>
                      <th className="border-r border-black px-2 py-2 text-xs font-bold text-center">UOM</th>
                      <th className="border-r border-black px-2 py-2 text-xs font-bold text-center">Ord.Qty</th>
                      <th className="border-r border-black px-2 py-2 text-xs font-bold text-center">Grn.Qty</th>
                      <th className="border-r border-black px-2 py-2 text-xs font-bold text-center">Accp.Qty</th>
                      <th className="border-r border-black px-2 py-2 text-xs font-bold text-center">Rej.Qty</th>
                      <th className="border-r border-black px-2 py-2 text-xs font-bold text-center">Ret.Qty</th>
                      <th className="border-r border-black px-2 py-2 text-xs font-bold text-center">UnitPrice</th>
                      <th className="px-2 py-2 text-xs font-bold text-center">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 && !loading ? (
                      <tr>
                        <td colSpan="11" className="text-center py-8 text-gray-500">
                          No items found
                        </td>
                      </tr>
                    ) : (
                      items.map((item) => (
                        <tr key={item.sno} className="border-b border-black">
                          <td className="border-r border-black px-2 py-2 text-xs text-center">{item.sno}</td>
                          <td className="border-r border-black px-2 py-2 text-xs">{item.itemCode}</td>
                          <td className="border-r border-black px-2 py-2 text-xs">{item.itemName}</td>
                          <td className="border-r border-black px-2 py-2 text-xs text-center">{item.uom}</td>
                          <td className="border-r border-black px-2 py-2 text-xs text-right">{item.ordQty}</td>
                          <td className="border-r border-black px-2 py-2 text-xs text-right">{item.grnQty}</td>
                          <td className="border-r border-black px-2 py-2 text-xs text-right">{item.accpQty}</td>
                          <td className="border-r border-black px-2 py-2 text-xs text-right">{item.rejQty}</td>
                          <td className="border-r border-black px-2 py-2 text-xs text-right">{item.retQty}</td>
                          <td className="border-r border-black px-2 py-2 text-xs text-right">{parseFloat(item.unitPrice).toFixed(2)}</td>
                          <td className="px-2 py-2 text-xs text-right">{parseFloat(item.amount).toFixed(2)}</td>
                        </tr>
                      ))
                    )}
                    
                    {/* Empty rows for consistent spacing */}
                    {items.length > 0 && [...Array(Math.max(0, 10 - items.length))].map((_, index) => (
                      <tr key={`empty-${index}`} className="border-b border-black h-8">
                        <td className="border-r border-black px-2 py-2"></td>
                        <td className="border-r border-black px-2 py-2"></td>
                        <td className="border-r border-black px-2 py-2"></td>
                        <td className="border-r border-black px-2 py-2"></td>
                        <td className="border-r border-black px-2 py-2"></td>
                        <td className="border-r border-black px-2 py-2"></td>
                        <td className="border-r border-black px-2 py-2"></td>
                        <td className="border-r border-black px-2 py-2"></td>
                        <td className="border-r border-black px-2 py-2"></td>
                        <td className="border-r border-black px-2 py-2"></td>
                        <td className="px-2 py-2"></td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Total Section */}
                {items.length > 0 && (
                  <div className="flex justify-end border-t-2 border-black p-2">
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-bold">Total Amount:</span>
                      <span className="text-sm font-bold">₹{calculateTotal()}</span>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Footer */}
          <div className="border-t-2 border-black mt-auto">
            <div className="flex justify-between items-center p-2">
              <div className="text-xs">
                Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
              </div>
              <div className="text-xs">
                Page 1 of 1
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Debug Panel - Hidden in print */}
      <div className="print:hidden mt-8 p-4 bg-gray-100 rounded no-print max-w-4xl mx-auto">
        <h3 className="font-bold mb-2">Debug Info:</h3>
        <div className="text-sm space-y-1">
          <p><strong>Report ID:</strong> {reportId}</p>
          <p><strong>API URL:</strong> https://www.nissi-india.com/api/getPurchaseReturnRpt_Print/{reportId}</p>
          <p><strong>Status:</strong> {loading ? 'Loading...' : error ? 'Error' : 'Success'}</p>
          <p><strong>Items Count:</strong> {items.length}</p>
          {error && <p><strong>Error:</strong> {error}</p>}
        </div>
      </div>
    </div>
  );
};

export default PurchaseReturnReportPrint;